import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// Solid event rendering without splitting into cells; absolute positioning
// Expects props: startDate, endDate, events (same shape as original scheduler { events: [...] })
// events.events contains objects with .type ('start'/'stop'), .pair, .playlist.playlistName, .when pattern

const HOUR_HEIGHT = 60; // px per hour
const MINUTE_HEIGHT = HOUR_HEIGHT / 60; // px per minute
const MIN_EVENT_PX = 2; // minimum rendered height for very short events
const HOUR_MARKER_INTERVAL = 60; // minutes
const LEFT_COL_WIDTH = 64; // px width for hours list column

const isSameDay = (a, b) =>
  a?.getFullYear() === b?.getFullYear() &&
  a?.getMonth() === b?.getMonth() &&
  a?.getDate() === b?.getDate();

const parseEventPairs = (raw) => {
  // console.error('🔍 [parseEventPairs] STARTING PARSE');
  if (!raw?.events) return [];
  const starts = raw.events.filter((e) => e.type === 'start');
  const stops = raw.events.filter((e) => e.type === 'stop');

  // console.error(
  //   '📊 [parseEventPairs] Found',
  //   starts.length,
  //   'starts and',
  //   stops.length,
  //   'stops'
  // );

  // Group stop events by pair and sort by time-of-day for robust matching
  const stopsByPair = stops.reduce((acc, e) => {
    if (!acc[e.pair]) acc[e.pair] = [];
    acc[e.pair].push(e);
    return acc;
  }, {});
  Object.keys(stopsByPair).forEach((pairId) => {
    stopsByPair[pairId].sort(
      (a, b) => extractTimeMinutes(a.when) - extractTimeMinutes(b.when),
    );
  });

  const pairs = starts
    .map((s) => {
      const candidates = stopsByPair[s.pair] || [];
      if (candidates.length === 0) {
        // console.warn(
        //   '[CalendarGridMinute] Missing stop for start event:',
        //   s.pair
        // );
        return null;
      }
      const sMin = extractTimeMinutes(s.when);
      // Pick the closest stop at or after the start time; fallback to the last in day
      let chosen =
        candidates.find((st) => extractTimeMinutes(st.when) >= sMin) ||
        candidates[candidates.length - 1];
      return { start: s, stop: chosen };
    })
    .filter(Boolean);

  // console.error('✅ [parseEventPairs] Created', pairs.length, 'pairs');

  // Split day-spanning pairs into multiple segments
  const splitPairs = [];
  pairs.forEach((pair, pairIndex) => {
    const startDate = extractDate(pair.start.when);
    const stopDate = extractDate(pair.stop.when);

    // console.error(
    //   `📅 [parseEventPairs] Pair ${pairIndex}: start="${startDate}" stop="${stopDate}"`
    // );

    if (startDate === stopDate) {
      // Same day: add as-is
      // console.error(`   → Same day, adding as-is`);
      splitPairs.push(pair);
    } else {
      // console.error(`   → Different days! Splitting...`);
      // Different days: split at midnight
      const startMin = extractTimeMinutes(pair.start.when);
      const stopMin = extractTimeMinutes(pair.stop.when);

      // First segment: from start time to end of start day (23:59:59)
      const firstSegmentStop = {
        ...pair.stop,
        when: pair.start.when.replace(
          pair.start.when.split(' ')[4],
          '23:59:59',
        ),
      };
      // console.error(
      //   `   → Adding first segment: ${
      //     pair.start.when.split(' ')[4]
      //   } to 23:59:59`
      // );
      splitPairs.push({
        start: pair.start,
        stop: firstSegmentStop,
        isSpanning: true,
      });

      // Middle segments: full days (00:00 to 23:59)
      const isMonthlyStart = isMonthlyRepeatable(pair.start.when);
      const isMonthlyStop = isMonthlyRepeatable(pair.stop.when);

      if (isMonthlyStart && isMonthlyStop) {
        // Monthly repeatable event (WWW MMM 10 RRRR to WWW MMM 15 RRRR)
        const startDay = extractDayOfMonth(pair.start.when);
        const stopDay = extractDayOfMonth(pair.stop.when);

        // console.error(
        //   `   → Monthly repeatable: day ${startDay} to day ${stopDay}`
        // );

        const dayDiff = stopDay - startDay;

        // console.error(`   → Day difference: ${dayDiff} days`);

        if (dayDiff > 1) {
          for (let day = startDay + 1; day < stopDay; day++) {
            const fullDayStart = {
              ...pair.start,
              when: `WWW MMM ${String(day).padStart(2, '0')} RRRR 00:00:00`,
            };
            const fullDayStop = {
              ...pair.stop,
              when: `WWW MMM ${String(day).padStart(2, '0')} RRRR 23:59:59`,
            };

            // console.error(`   → Adding middle segment: day ${day}`);

            splitPairs.push({
              start: fullDayStart,
              stop: fullDayStop,
              isSpanning: true,
            });
          }
        }
      } else {
        // Weekly repeatable event (Mon MMM DD RRRR to Tue MMM DD RRRR)
        const startDow = extractDayOfWeek(pair.start.when);
        const stopDow = extractDayOfWeek(pair.stop.when);
        const startDowIndex = dayOfWeekToIndex(startDow);
        const stopDowIndex = dayOfWeekToIndex(stopDow);

        // console.error(
        //   `   → Day-of-week: start="${startDow}"(${startDowIndex}) stop="${stopDow}"(${stopDowIndex})`
        // );

        // Determine how many days apart (accounting for week wrapping)
        let dayDiff = stopDowIndex - startDowIndex;
        if (dayDiff <= 0) dayDiff += 7; // Handle week boundary crossing

        // console.error(`   → Day difference: ${dayDiff} days`);

        // If more than 1 day apart, create intermediate full-day segments
        if (dayDiff > 1) {
          for (let i = 1; i < dayDiff; i++) {
            const intermediateIndex = (startDowIndex + i) % 7;
            const daysOfWeek = [
              'Sun',
              'Mon',
              'Tue',
              'Wed',
              'Thu',
              'Fri',
              'Sat',
            ];
            const intermediateDow = daysOfWeek[intermediateIndex];

            const fullDayStart = {
              ...pair.start,
              when: `${intermediateDow} MMM DD RRRR 00:00:00`,
            };
            const fullDayStop = {
              ...pair.stop,
              when: `${intermediateDow} MMM DD RRRR 23:59:59`,
            };

            // console.error(
            //   `   → Adding middle segment ${i}: ${intermediateDow}`
            // );

            splitPairs.push({
              start: fullDayStart,
              stop: fullDayStop,
              isSpanning: true,
            });
          }
        }
      }

      // Last segment: from start of stop day to stop time
      const lastSegmentStart = {
        ...pair.start,
        when: pair.stop.when.replace(pair.stop.when.split(' ')[4], '00:00:00'),
      };
      // console.error(
      //   `   → Adding last segment: 00:00:00 to ${pair.stop.when.split(' ')[4]}`
      // );
      splitPairs.push({
        start: lastSegmentStart,
        stop: pair.stop,
        isSpanning: true,
      });
    }
  });

  // console.error(
  //   '🎯 [CalendarGridMinute] Parsed',
  //   pairs.length,
  //   'pairs from',
  //   raw.events?.length,
  //   'events,',
  //   'expanded to',
  //   splitPairs.length,
  //   'segments'
  // );
  return splitPairs;
};

const extractTimeMinutes = (when) => {
  // when format: WWW MMM DD RRRR HH:MM:SS
  try {
    const timePart = when.split(' ')[4];
    const [hh, mm] = timePart.split(':');
    return Number(hh) * 60 + Number(mm);
  } catch {
    return 0;
  }
};

const extractDate = (when) => {
  // Extract date part: WWW MMM DD RRRR
  try {
    const parts = when.split(' ');
    return parts.slice(0, 4).join(' '); // "WWW MMM DD RRRR"
  } catch {
    return '';
  }
};

const extractDayOfWeek = (when) => {
  // Extract day of week from start: "Mon MMM DD RRRR HH:MM:SS" -> "Mon"
  try {
    const parts = when.split(' ');
    return parts[0]; // "Mon", "Tue", etc.
  } catch {
    return '';
  }
};

const extractDayOfMonth = (when) => {
  // Extract day of month: "WWW MMM 15 RRRR HH:MM:SS" -> 15
  try {
    const parts = when.split(' ');
    const dayPart = parts[2];
    if (dayPart === 'DD') return null; // Placeholder, not a specific day
    const day = parseInt(dayPart, 10);
    return isNaN(day) ? null : day;
  } catch {
    return null;
  }
};

const isMonthlyRepeatable = (when) => {
  return extractDayOfMonth(when) !== null;
};

const dayOfWeekToIndex = (dow) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days.indexOf(dow);
};

const dayMatches = (date, when) => {
  // Extract the date part from when string (first 15 chars: "WWW MMM DD RRRR" or actual date)
  const whenDatePart = when.substring(0, 15);

  // Get the date string representation
  const dStr = date.toString();
  const dateFormattedPart = dStr.substring(0, 15); // "WWW MMM DD RRRR"

  // Direct comparison of actual date strings
  if (whenDatePart === dateFormattedPart) {
    return true;
  }

  // Check for monthly repeatable (WWW MMM 15 RRRR)
  const dayOfMonth = extractDayOfMonth(when);
  if (dayOfMonth !== null) {
    // Monthly repeatable: match if day of month matches
    return date.getDate() === dayOfMonth;
  }

  // Fallback: try placeholder replacement for original event format
  const replaced = whenDatePart
    .replace('WWW', dStr.slice(0, 3))
    .replace('MMM', dStr.slice(4, 7))
    .replace('DD', String(date.getDate()).padStart(2, '0'))
    .replace('RRRR', String(date.getFullYear()));

  return replaced === dateFormattedPart;
};

const DayColumn = ({ date, pairs, themeColor, getPairAndOpenDetails }) => {
  const isToday = date.toDateString() === new Date().toDateString();
  const dayPairs = pairs.filter((p) => dayMatches(date, p.start.when));
  // console.log(
  //   '[DayColumn]',
  //   date.toDateString(),
  //   'has',
  //   dayPairs.length,
  //   'pairs'
  // );
  return (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        minWidth: 150,
        height: HOUR_HEIGHT * 24,
        backgroundColor: 'transparent',
      }}
    >
      {/* hour markers */}
      {Array.from({ length: 24 }, (_, h) => (
        <Box
          key={h}
          sx={{
            position: 'absolute',
            top: h * HOUR_HEIGHT,
            left: 0,
            right: 0,
            borderBottom: '1px solid #1C2536',
          }}
        />
      ))}
      {dayPairs.map((p, index) => {
        const startM = extractTimeMinutes(p.start.when);
        const stopM = extractTimeMinutes(p.stop.when);
        const duration = Math.max(stopM - startM, 1);
        return (
          <Tooltip
            key={p.start.pair}
            title={`${p.start.playlist.playlistName} (${
              p.start.when.split(' ')[4]
            } - ${p.stop.when.split(' ')[4]})`}
          >
            <Box
              component='button'
              className={index + 1}
              onClick={() =>
                getPairAndOpenDetails && getPairAndOpenDetails(p.start)
              }
              sx={{
                position: 'absolute',
                top: startM * MINUTE_HEIGHT,
                left: 4,
                right: 4,
                height: Math.max(duration * MINUTE_HEIGHT, MIN_EVENT_PX),
                backgroundColor: themeColor || '#1C2536',
                border: '1px solid ' + (themeColor || '#3a4b5e'),
                color: '#fff',
                fontSize: '0.65rem',
                textAlign: 'left',
                padding: '2px 4px',
                borderRadius: '10px',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              <Typography variant='caption' noWrap>
                {Math.max(duration * MINUTE_HEIGHT, MIN_EVENT_PX) > 20
                  ? p.start.playlist.playlistName
                  : ''}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const CalendarGridMinute = ({
  startDate,
  endDate,
  events,
  themeColor,
  getPairAndOpenDetails,
  setDay,
}) => {
  const theme = useTheme();
  const [pairs, setPairs] = useState([]);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // console.log('[CalendarGridMinute] Events changed:', events);
    // console.log(parseEventPairs(events));
    setPairs(parseEventPairs(events));
  }, [events]);

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
  const isMonthView = diffDays > 7; // threshold similar to original (> one week)
  const viewType = isMonthView ? 'month' : diffDays > 0 ? 'week' : 'day';

  useEffect(() => {
    if (!setDay) return;
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (viewType === 'month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDay({ startDate: monthStart, endDate: monthEnd });
      return;
    }

    if (viewType === 'week') {
      const WEEK_START = 1; // Monday
      const weekStart = new Date(now);
      const offset = (weekStart.getDay() - WEEK_START + 7) % 7;
      weekStart.setDate(weekStart.getDate() - offset);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      setDay({ startDate: weekStart, endDate: weekEnd });
      return;
    }

    // Day view: don't override user-selected dates
    // The date is set by clicking a day in month view
  }, [viewType, setDay]);

  useEffect(() => {
    if (viewType === 'month') return;
    if (!scrollContainerRef.current || pairs.length === 0) return;

    const visibleStart = new Date(startDate);
    const visibleEnd = new Date(endDate);
    const visiblePairs = pairs.filter((pair) => {
      const startsOnVisibleDay = dayMatches(visibleStart, pair.start.when);
      if (startsOnVisibleDay) return true;

      const cursor = new Date(visibleStart);
      while (cursor <= visibleEnd) {
        if (dayMatches(cursor, pair.start.when)) return true;
        cursor.setDate(cursor.getDate() + 1);
      }
      return false;
    });

    if (visiblePairs.length === 0) return;

    const firstEventMinute = visiblePairs.reduce((minMinute, pair) => {
      const eventMinute = extractTimeMinutes(pair.start.when);
      return Math.min(minMinute, eventMinute);
    }, 24 * 60);

    const targetScrollTop = Math.max(firstEventMinute * MINUTE_HEIGHT - 24, 0);

    const rafId = window.requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = targetScrollTop;
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [pairs, startDate, endDate, viewType]);

  if (isMonthView) {
    // Month view with correct weekday alignment (Monday-first) and robust event matching
    const WEEK_START = 1; // 0 = Sunday, 1 = Monday
    const daysWeek =
      WEEK_START === 1
        ? [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday',
          ]
        : [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ];

    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);

    const getWeekOffset = (date) => (date.getDay() - WEEK_START + 7) % 7;
    const getGridStart = (date) => {
      const result = new Date(date);
      result.setDate(result.getDate() - getWeekOffset(result));
      return result;
    };
    const getGridEnd = (date) => {
      const result = new Date(date);
      const offset = 6 - getWeekOffset(result);
      result.setDate(result.getDate() + offset);
      return result;
    };

    const gridStart = getGridStart(monthStart);
    const gridEnd = getGridEnd(monthEnd);

    const weeks = [];
    let cursor = new Date(gridStart);
    let currentWeek = [];

    while (cursor <= gridEnd) {
      currentWeek.push(new Date(cursor));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    // Helper to get events for a date (dedupe by playlist id, use start events only)
    const getEventsForDate = (date) => {
      if (!date || date.getMonth() !== start.getMonth()) return [];

      // Filter parsed pairs that match this date
      const matchedPairs = pairs.filter((p) => dayMatches(date, p.start.when));

      // Deduplicate by playlist ID while keeping start/stop for tooltip
      const unique = [];
      matchedPairs.forEach((pair) => {
        const exists = unique.find(
          (u) => u.start.playlist?._id === pair.start.playlist?._id,
        );
        if (!exists) {
          unique.push(pair);
        }
      });

      return unique;
    };

    const MonthCell = ({ date }) => {
      if (!date)
        return (
          <Box
            sx={{ width: '100%', height: 100, border: '1px solid #1C2536' }}
          />
        );
      const isCurrentMonth = date.getMonth() === start.getMonth();
      const dateEvents = getEventsForDate(date);
      return (
        <Box
          sx={{
            width: '100%',
            height: 100,
            border: '1px solid #1C2536',
            p: 0.5,
            position: 'relative',
            cursor: 'pointer',
            backgroundColor: isCurrentMonth
              ? 'transparent'
              : theme.palette.mode === 'dark'
                ? '#0f1726'
                : '#f0f2f7',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? '#182233' : '#dfe3eb',
            },
          }}
          onClick={() =>
            isCurrentMonth &&
            setDay &&
            setDay({ startDate: date, endDate: date })
          }
        >
          <Typography
            variant='caption'
            sx={{
              position: 'absolute',
              top: 4,
              right: 6,
              opacity: isCurrentMonth ? 0.7 : 0.4,
            }}
          >
            {date.getDate()}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 3 }}>
            {dateEvents.map((ev) => {
              const startTime = ev.start.when.split(' ')[4];
              const stopTime = ev.stop.when.split(' ')[4];
              const title =
                startTime == '00:00:00' && stopTime == '23:59:59'
                  ? `${ev.start.playlist?.playlistName || ''} (entire day)`
                  : `${ev.start.playlist?.playlistName || ''} (${startTime} - ${stopTime})`;
              return (
                <Tooltip key={ev.start.pair} title={title}>
                  <Box
                    component='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      getPairAndOpenDetails && getPairAndOpenDetails(ev.start);
                    }}
                    sx={{
                      background: 'none',
                      border: 'none',
                      p: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <FiberManualRecordIcon
                      fontSize='small'
                      sx={{ color: themeColor || 'secondary.main' }}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      );
    };

    return (
      <Box
        ref={scrollContainerRef}
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'auto',
          height: 'calc(100vh - 400px)',
        }}
      >
        <Box sx={{ minWidth: 'max-content' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 150px)',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#121b2bad' : '#ffffff',
              borderBottom: '1px solid #1C2536',
            }}
          >
            {daysWeek.map((d) => (
              <Box
                key={d}
                sx={{ textAlign: 'center', py: 1, minWidth: '150px' }}
              >
                <Typography variant='caption' sx={{ fontWeight: 600 }}>
                  {d}
                </Typography>
              </Box>
            ))}
          </Box>
          {/* Weeks rendered row by row to preserve alignment */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {weeks.map((week, wi) => (
              <Box
                key={`week-${wi}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 150px)',
                }}
              >
                {week.map((cellDate, di) => (
                  <MonthCell key={`cell-${wi}-${di}`} date={cellDate} />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Day / Week view (minute precision vertical columns)
  const days = [];
  for (let d = 0; d <= diffDays; d++) {
    const day = new Date(start);
    day.setDate(start.getDate() + d);
    days.push(day);
  }

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        height: 'calc(100vh - 400px)',
      }}
    >
      <Box sx={{ minWidth: 'max-content' }}>
        {/* Sticky header with left spacer for hours column */}
        <Box
          sx={{
            display: 'flex',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor:
              theme.palette.mode === 'dark' ? '#121b2bad' : '#ffffff',
          }}
        >
          <Box
            sx={{
              width: LEFT_COL_WIDTH,
              flex: '0 0 auto',
              borderRight: '1px solid #1C2536',
            }}
          />
          <Box
            sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}
          >
            {days.map((d) => (
              <Box
                key={d.toISOString()}
                sx={{ flex: 1, textAlign: 'center', minWidth: 150 }}
              >
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: themeColor }}
                >
                  {d.toDateString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Left sticky hours list column */}
          <Box
            sx={{
              flex: '0 0 auto',
              width: LEFT_COL_WIDTH,
              position: 'sticky',
              left: 0,
              zIndex: 1,
              border: '1px solid #1C2536',
              backgroundColor:
                theme.palette.mode === 'dark' ? '#0f1726' : '#f7f9fb',
            }}
          >
            <Box sx={{ position: 'relative', height: HOUR_HEIGHT * 24 }}>
              {Array.from({ length: 24 }, (_, h) => (
                <Typography
                  key={h}
                  variant='caption'
                  sx={{
                    position: 'absolute',
                    top: h * HOUR_HEIGHT - 6,
                    left: 6,
                    opacity: 0.8,
                  }}
                >
                  {String(h).padStart(2, '0')}:00
                </Typography>
              ))}
            </Box>
          </Box>
          {/* Day columns with space-between layout */}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            {days.map((d) => (
              <DayColumn
                key={d.toISOString()}
                date={d}
                pairs={pairs}
                themeColor={themeColor}
                getPairAndOpenDetails={getPairAndOpenDetails}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CalendarGridMinute;
